import { buttonStyle } from 'components/buttons/buttonStyle';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput.tsx';
import { DataTable } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useId, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useUpdateDefaultAdminFeePercentageMutation } from 'store/api/tenants.ts';
import { TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

interface DefaultAdminFeeForm {
    defaultAdminFeePercentage: string;
}

/** The admin fee the contract form fills in on a service agreement once the Admin fee permitted keyword is picked. */
const DefaultAdminFeePercentageControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateDefaultAdminFeePercentage] = useUpdateDefaultAdminFeePercentageMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const hintId = useId();

    const { handleSubmit, control } = useForm<DefaultAdminFeeForm>({
        defaultValues: {
            defaultAdminFeePercentage: tenantConfiguration.defaultAdminFeePercentage?.toString() ?? '',
        },
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.default-admin-fee'),
                id: 'default-admin-fee',
                enableSorting: false,
                cell: () => (
                    <div className="new:grid-cols-[16.25rem_auto_1fr] grid grid-cols-[19.75rem_auto_1fr] items-center gap-x-10 gap-y-2">
                        <p className="text-body2">
                            <FormattedMessage id="configuration.default-admin-fee-percentage" />
                        </p>
                        <PercentageInput
                            control={control}
                            name="defaultAdminFeePercentage"
                            decimalPlaces={2}
                            maxWholeDigits={4}
                            inputProps={{
                                min: 0,
                                'aria-label': t('configuration.default-admin-fee-percentage'),
                                'aria-describedby': hintId,
                            }}
                            disabled={!hasUpdatePermission}
                            wrapperClassName="w-24"
                        />
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-8 justify-self-start')}
                            onClick={() => {
                                void handleSubmit(({ defaultAdminFeePercentage }) =>
                                    updateDefaultAdminFeePercentage({
                                        defaultAdminFeePercentage:
                                            defaultAdminFeePercentage === '' ? null : Number(defaultAdminFeePercentage),
                                    })
                                )();
                            }}
                            disabled={!hasUpdatePermission}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                        <p
                            id={hintId}
                            className="text-caption text-muted-foreground col-span-2 col-start-2 text-pretty"
                        >
                            <FormattedMessage id="configuration.default-admin-fee-hint" />
                        </p>
                    </div>
                ),
            },
        ],
        [control, handleSubmit, hasUpdatePermission, hintId, updateDefaultAdminFeePercentage]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.default-admin-fee-control" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-default-admin-fee"
            />
        </>
    );
};

export default DefaultAdminFeePercentageControl;
